import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Breadcrumbs from "../BreadCrum/BreadCrum";
import PageHeadingSection from "../pageheading/PageHeadingSection";
import CommonTable from "../table/ComonTable";
import { emergencyApi, readSession, apiMessage } from "./emergencyApi";

const EmergencyReports = () => {
    const { user } = useSelector((state) => state.auth);
    const { siteId } = readSession(user);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [history, setHistory] = useState([]);
    const [summary, setSummary] = useState(null);
    const [guards, setGuards] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        const params = {
            site_id: siteId,
            from_date: fromDate || undefined,
            to_date: toDate || undefined,
            page: 1,
            page_size: 50
        };
        try {
            const [historyRes, summaryRes, guardRes] = await Promise.all([
                emergencyApi.history(params),
                emergencyApi.summary(params),
                emergencyApi.guardPerformance(params)
            ]);
            setHistory(historyRes.data?.data || []);
            setSummary(summaryRes.data || null);
            setGuards((guardRes.data?.data || []).map((item) => ({ ...item, id: item.guard_user_id })));
        } catch (error) {
            toast.error(apiMessage(error, "Failed to load reports"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs items={["Emergency Services", "Reports"]} />
            <PageHeadingSection mainHeading="Emergency reports" subHeading="History, summary, and guard performance for this site." />
            <div className="mt-4 flex flex-wrap gap-3 items-end">
                <label className="text-xs font-semibold text-textprimary">From
                    <input type="date" className="mt-1 block border border-bordercolor rounded-md px-3 py-2 text-sm" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
                </label>
                <label className="text-xs font-semibold text-textprimary">To
                    <input type="date" className="mt-1 block border border-bordercolor rounded-md px-3 py-2 text-sm" value={toDate} onChange={(event) => setToDate(event.target.value)} />
                </label>
                <button type="button" className="border border-bordercolor rounded-md px-3 py-2 text-xs" onClick={load} disabled={loading}>{loading ? "Loading" : "Load reports"}</button>
            </div>
            {summary && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div className="border border-bordercolor rounded-md p-3">Raised: {summary.total_raised ?? 0}</div>
                    <div className="border border-bordercolor rounded-md p-3">Active: {summary.total_active ?? 0}</div>
                    <div className="border border-bordercolor rounded-md p-3">Resolved: {summary.total_resolved ?? 0}</div>
                    <div className="border border-bordercolor rounded-md p-3">Avg minutes: {summary.avg_resolution_time_minutes ?? "—"}</div>
                </div>
            )}
            <div className="mt-4">
                <CommonTable
                    tableHeading="History"
                    loading={loading}
                    headers={[
                        { label: "TYPE", key: "type" },
                        { label: "STATUS", key: "status" },
                        { label: "RAISED BY", key: "raisedBy" },
                        { label: "RAISED ON", key: "raisedOn" }
                    ]}
                    data={history.map((item) => ({ ...item, id: item.emergency_id }))}
                    renderCell={(key, row) => {
                        if (key === "type") return row.emergency_type_name || "—";
                        if (key === "status") return row.status || "—";
                        if (key === "raisedBy") return row.raised_by_user_name || "—";
                        return row.raised_on ? new Date(row.raised_on).toLocaleString() : "—";
                    }}
                />
            </div>
            <div className="mt-4">
                <CommonTable
                    tableHeading="Guard performance"
                    headers={[
                        { label: "GUARD", key: "guard" },
                        { label: "ASSIGNED", key: "assigned" },
                        { label: "RESOLVED", key: "resolved" },
                        { label: "PENDING", key: "pending" }
                    ]}
                    data={guards}
                    renderCell={(key, row) => {
                        if (key === "guard") return row.guard_name || "—";
                        if (key === "assigned") return row.total_assigned ?? 0;
                        if (key === "resolved") return row.total_resolved_by_guard ?? 0;
                        return row.total_pending ?? 0;
                    }}
                />
            </div>
        </div>
    );
};

export default EmergencyReports;
